import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { motion, AnimatePresence } from 'motion/react'
import { Send, X, CheckCircle, Clock } from 'lucide-react'
import type { SentEmailData } from '../../../shared/types/chat'

interface SentEmailCardProps {
  email: SentEmailData
}

export default function SentEmailCard({ email }: SentEmailCardProps) {
  const { t } = useTranslation('chat')
  const [open, setOpen] = useState(false)

  return (
    <>
      <button
        onClick={() => setOpen(true)}
        className="flex flex-col gap-1 px-3 py-2 rounded-xl border border-emerald/20 bg-emerald/5 hover:bg-emerald/10 hover:border-emerald/30 transition-colors text-left w-full"
      >
        <div className="flex items-center gap-2 w-full">
          <Send size={14} className="text-emerald/70 flex-shrink-0" />
          <span className="text-sm text-emerald/90 truncate flex-1">{t('sentEmail.to')} {email.to}</span>
          {email.status === 'delivered' ? (
            <span className="flex items-center gap-1 text-xs text-emerald/60 flex-shrink-0">
              <CheckCircle size={10} />
              {t('sentEmail.delivered')}
            </span>
          ) : (
            <span className="flex items-center gap-1 text-xs text-pearl/60 flex-shrink-0">
              <Clock size={10} />
              {t('sentEmail.sent')}
            </span>
          )}
        </div>
        <div className="pl-[22px]">
          <span className="text-xs text-pearl/50 truncate block">{email.subject}</span>
        </div>
      </button>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 flex items-end sm:items-center justify-center modal-backdrop bg-black/60"
            onClick={() => setOpen(false)}
          >
            <motion.div
              initial={{ y: '100%' }}
              animate={{ y: 0 }}
              exit={{ y: '100%' }}
              transition={{ type: 'spring', damping: 28, stiffness: 300 }}
              onClick={(e) => e.stopPropagation()}
              className="w-full max-w-lg bg-charcoal border-t border-gold/20 rounded-t-2xl p-5 pb-8 max-h-[70dvh] overflow-y-auto overscroll-contain sm:border sm:rounded-2xl sm:pb-5 sm:shadow-2xl sm:max-h-[80dvh]"
            >
              <div className="flex items-start justify-between mb-4">
                <div className="flex-1 min-w-0">
                  <h3 className="text-base font-medium text-ivory truncate">{email.subject}</h3>
                  <p className="text-xs text-pearl/50 mt-0.5">
                    {t('sentEmail.to')} {email.to} &lt;{email.toEmail}&gt;
                  </p>
                </div>
                <button
                  onClick={() => setOpen(false)}
                  className="ml-3 p-1.5 rounded-lg text-pearl/60 hover:text-ivory hover:bg-white/5 transition-colors flex-shrink-0"
                >
                  <X size={18} />
                </button>
              </div>

              <div className="flex items-center gap-1.5 mb-3 text-emerald/70">
                {email.status === 'delivered' ? <CheckCircle size={13} /> : <Clock size={13} />}
                <span className="text-xs">{email.status === 'delivered' ? t('sentEmail.delivered') : t('sentEmail.sent')}</span>
              </div>

              <div className="h-px bg-gold/10 mb-4" />

              <div className="text-sm text-ivory/80 leading-relaxed whitespace-pre-line">
                {email.preview}
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  )
}
